import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";

// Refresh unread notification count every 30 seconds in the background.
const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

export default function NotificationBell(props = {}) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items.slice(0, 6));
      setUnread(data.unread);
    } catch (err) { /* notifications fetch is non-critical */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, NOTIFICATIONS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    await load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 grid place-items-center border border-zinc-200 hover:bg-zinc-100 transition-colors"
        data-testid={props["data-testid"] || "notification-bell"}
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#94959A] text-white text-[10px] font-bold grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-80 max-h-[420px] overflow-auto bg-white border border-zinc-300 shadow-[4px_4px_0_0_#C8102E] z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
            <div className="font-display font-black text-sm tracking-tight">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all" className="text-[10px] uppercase tracking-widest font-semibold text-[#C8102E] hover:underline">
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-zinc-500">No notifications yet.</div>
          )}
          {items.map((n) => (
            <div key={n.notification_id} className="px-3 py-2.5 border-b border-zinc-100 flex items-start gap-2 hover:bg-zinc-50">
              <div className={`w-1.5 h-1.5 mt-1.5 ${n.read ? "bg-zinc-300" : "bg-[#C8102E]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{n.title}</div>
                {n.body && <div className="text-xs text-zinc-500 line-clamp-2">{n.body}</div>}
              </div>
            </div>
          ))}
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs uppercase tracking-widest font-semibold text-[#C8102E] py-3 hover:bg-zinc-50"
            data-testid="notif-view-all"
          >
            View all →
          </Link>
        </div>
      )}
    </div>
  );
}
